import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPrescriber } from '../../../../apis/clinics/requests';
import { useSnackbar } from 'notistack';
import { PrescribingProtocol } from '../../../../apis/users';

interface PrescriberFormData {
  forename?: string;
  surname?: string;
  email?: string;
  nmcNumber?: string;
  gphcNumber?: string;
  address?: string;
  postcode?: string;
  telephone?: string;
  roles: string[];
  clinic_name?: string;
  registration_body?: string;
  prescribing_protocols?: PrescribingProtocol[];
}

interface UseCreatePrescriberReturn {
  addPrescriber: (data: PrescriberFormData) => Promise<void>;
  isLoading: boolean;
}

const useCreatePrescriber = (clinicId: string): UseCreatePrescriberReturn => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync, isLoading } = useMutation(
    (data: PrescriberFormData) => {
      const payload = {
        username: data.email || '',
        forename: data.forename || '',
        surname: data.surname || '',
        roles: data.roles,
        ...(data.telephone?.trim() && { telephone: data.telephone.trim() }),
        ...(data.address?.trim() && { address: data.address.trim() }),
        ...(data.postcode?.trim() && { postcode: data.postcode.trim() }),
        ...(data.nmcNumber?.trim() && { nmc_number: data.nmcNumber.trim() }),
        ...(data.gphcNumber?.trim() && { gphc_number: data.gphcNumber.trim() }),
        ...(data.clinic_name?.trim() && { clinic_name: data.clinic_name.trim() }),
        ...(data.registration_body?.trim() && { registration_body: data.registration_body.trim() }),
        ...(data.prescribing_protocols?.length && { prescribing_protocols: data.prescribing_protocols })
      };

      return createPrescriber(clinicId, payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['prescribers', clinicId]);
        enqueueSnackbar('Prescriber has been added', { variant: 'success' });
      },
      onError: () => {
        enqueueSnackbar('Failed to add prescriber', { variant: 'error' });
      }
    }
  );

  return {
    addPrescriber: mutateAsync,
    isLoading
  };
};

export default useCreatePrescriber; 
